'use client';

import { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import dynamic from 'next/dynamic';

import { queryClient } from '@/lib/query-client';
import { NotificationProvider } from '@/contexts/notification-context';
import { AuthProvider } from '@/components/auth/auth-provider';
import { ChatbotProvider } from '@/features/chatbot';
import { FormPrefillProvider } from '@/features/chatbot/providers/FormPrefillProvider';

/**
 * RULE 15: Proper fix for Chakra UI v3 + Next.js hydration error
 *
 * Root Cause: Chakra UI v3 uses Emotion CSS-in-JS which injects <style> tags on server.
 * On client, React expects the same DOM structure but finds different elements,
 * causing hydration mismatch that crashes the entire React tree.
 *
 * Solution: Dynamic import with ssr: false prevents server-side rendering of ChakraProvider.
 * This eliminates hydration mismatch because there's no server HTML to mismatch.
 *
 * Trade-off: Initial page load shows unstyled content briefly (FOUC).
 * This is acceptable per Chakra UI team recommendation (GitHub Discussion #9051).
 *
 * @see https://github.com/chakra-ui/chakra-ui/discussions/9051
 * @see https://chakra-ui.com/docs/get-started/frameworks/next-app
 */
const UIProvider = dynamic(() => import('@axon/ui').then((mod) => ({ default: mod.Provider })), {
  ssr: false,
  loading: () => (
    // Minimal loading state to reduce FOUC impact
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'bg.subtle',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--chakra-colors-border-default)',
          borderTopColor: 'blue.500',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  ),
});

// Conditionally import ReactQueryDevtools only in development
const ReactQueryDevtools = dynamic(
  () =>
    import('@tanstack/react-query-devtools').then((mod) => ({
      default: mod.ReactQueryDevtools,
    })),
  { ssr: false }
);

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  return (
    <UIProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <NotificationProvider>
            <FormPrefillProvider>
              <ChatbotProvider>{children}</ChatbotProvider>
            </FormPrefillProvider>
          </NotificationProvider>
        </AuthProvider>
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </UIProvider>
  );
}
